import React, { useState } from 'react';
import { Alert, Button, Card, Checkbox, Form, Input, Space, Typography } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import PasswordStrengthValidator, { PasswordValidation } from './PasswordStrengthValidator';
import RecaptchaWidget from './RecaptchaWidget';
import { createSanitizedFormHandler, getFormSanitizers } from '../../utils/xssProtection';
import '../../style/RegisterForm.css';

const { Title, Text } = Typography;

interface RegisterFormProps {
    onBackToLogin: () => void;
}

interface RegistrationValues {
    email: string;
    password: string;
    confirmPassword: string;
    gdprConsent: boolean;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onBackToLogin }) => {
    const [form] = Form.useForm<RegistrationValues>();
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submittedEmail, setSubmittedEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [passwordValidation, setPasswordValidation] = useState<PasswordValidation | null>(null);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [captchaKey, setCaptchaKey] = useState(0);
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
    const recaptchaEnabled = String(process.env.REACT_APP_REGISTRATION_CAPTCHA_ENABLED || '').trim().toLowerCase() === 'true';
    const recaptchaActive = recaptchaEnabled && Boolean(recaptchaSiteKey);
    const resetCaptcha = () => {
        setCaptchaToken(null);
        setCaptchaKey((current) => current + 1);
    };

    const handleRegister = async (values: RegistrationValues) => {
        if (!passwordValidation?.isValid) {
            setError('Please ensure your password meets all security requirements.');
            return;
        }
        if (recaptchaActive && !captchaToken) {
            setError('Please complete captcha verification before registering.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        const normalizedEmail = String(values.email || '').trim().toLowerCase();

        try {
            const response = await axios.post(ENDPOINTS.REGISTER, {
                email: normalizedEmail,
                password: values.password,
                confirmPassword: values.confirmPassword,
                gdprConsent: values.gdprConsent,
                captchaToken: captchaToken || undefined,
            });
            setSubmittedEmail(normalizedEmail);
            setSuccess(response.data?.message || 'Check your email to verify your account.');
            resetCaptcha();
            form.resetFields();
            setCurrentPassword('');
            setPasswordValidation(null);
        } catch (err: any) {
            const responseData = err?.response?.data;
            if (responseData?.flow === 'verification_pending') {
                setSubmittedEmail(normalizedEmail);
            }
            setError(responseData?.message || responseData?.error || 'Registration failed.');
            resetCaptcha();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!submittedEmail) return;
        if (recaptchaActive && !captchaToken) {
            setError('Please complete captcha verification before requesting another email.');
            return;
        }
        setResendLoading(true);
        setError('');
        try {
            const response = await axios.post(ENDPOINTS.RESEND_REGISTRATION_VERIFICATION, {
                email: submittedEmail,
                captchaToken: captchaToken || undefined,
            });
            setSuccess(response.data?.message || 'A new verification link has been sent.');
            resetCaptcha();
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Unable to resend the verification email.');
            resetCaptcha();
        } finally {
            setResendLoading(false);
        }
    };

    const sanitizedRegister = createSanitizedFormHandler(handleRegister, getFormSanitizers());

    if (submittedEmail) {
        return (
            <div className="register-form-shell">
                <Card className="register-complete-card" style={{ maxWidth: 680, margin: '0 auto' }}>
                    <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
                        <div className="register-complete-icon"><CheckOutlined /></div>
                        <Title level={2}>Check your email</Title>
                        <Text>
                            We sent an account-verification link to <strong>{submittedEmail}</strong>.
                            No company has been created.
                        </Text>
                        {error && <Alert message={error} type="error" showIcon />}
                        {success && <Alert message={success} type="success" showIcon />}
                        {recaptchaActive && (
                            <RecaptchaWidget key={captchaKey} siteKey={recaptchaSiteKey} onTokenChange={setCaptchaToken} />
                        )}
                        <Space wrap style={{ justifyContent: 'center' }}>
                            <Button type="primary" onClick={onBackToLogin}>Back to Login</Button>
                            <Button loading={resendLoading} onClick={handleResend}>Resend verification</Button>
                            <Button onClick={() => {
                                setSubmittedEmail('');
                                setError('');
                                setSuccess('');
                                resetCaptcha();
                            }}>
                                Use another email
                            </Button>
                        </Space>
                    </Space>
                </Card>
            </div>
        );
    }

    return (
        <div className="register-form-shell">
            <Form
                form={form}
                name="register-user"
                layout="vertical"
                size="large"
                onFinish={sanitizedRegister}
                className="register-main-flow"
            >
                <Card className="register-card" style={{ maxWidth: 720, margin: '0 auto' }}>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div className="register-details-header">
                            <Button
                                type="text"
                                icon={<ArrowLeftOutlined />}
                                shape="circle"
                                aria-label="Back to login"
                                className="register-back-button"
                                onClick={onBackToLogin}
                            />
                            <div className="register-details-title">
                                <Title level={2}><UserOutlined /> Register for OSCAR</Title>
                                <Text type="secondary">Create your user account. Company setup happens after login.</Text>
                            </div>
                            <span className="register-details-header-spacer" />
                        </div>

                        {error && <Alert message={error} type="error" showIcon />}

                        <div className="register-account-step">
                            <Form.Item
                                name="email"
                                rules={[
                                    { required: true, message: 'Please enter your email.' },
                                    { type: 'email', message: 'Please enter a valid email address.' },
                                ]}
                            >
                                <Input prefix={<MailOutlined />} placeholder="Email" autoComplete="email" />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[
                                    { required: true, message: 'Please enter a password.' },
                                    { min: 8, message: 'Password must be at least 8 characters.' },
                                    {
                                        validator: (_, value) => !value || passwordValidation?.isValid
                                            ? Promise.resolve()
                                            : Promise.reject(new Error('Password does not meet the security requirements.')),
                                    },
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Password"
                                    autoComplete="new-password"
                                    onChange={(event) => setCurrentPassword(event.target.value)}
                                />
                            </Form.Item>

                            {currentPassword && (
                                <PasswordStrengthValidator
                                    password={currentPassword}
                                    onValidationChange={setPasswordValidation}
                                    showRequirements
                                />
                            )}

                            <Form.Item
                                name="confirmPassword"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Please confirm your password.' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            return !value || getFieldValue('password') === value
                                                ? Promise.resolve()
                                                : Promise.reject(new Error('Passwords do not match.'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Confirm password"
                                    autoComplete="new-password"
                                />
                            </Form.Item>

                            {recaptchaActive && (
                                <RecaptchaWidget key={captchaKey} siteKey={recaptchaSiteKey} onTokenChange={setCaptchaToken} />
                            )}

                            <Form.Item
                                name="gdprConsent"
                                valuePropName="checked"
                                rules={[{
                                    validator: (_, value) => value
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('GDPR consent is required.')),
                                }]}
                            >
                                <Checkbox>
                                    <Text style={{ fontSize: 12 }}>
                                        I consent to the processing of my personal data in accordance with the
                                        <a href="https://www.e-nano.io/samsss" target="_blank" rel="noopener noreferrer"> SaaS Availability, Maintenance &amp; Support Services Terms</a> and
                                        <a href="https://www.e-nano.io/deviceandsaassubscriberterms" target="_blank" rel="noopener noreferrer"> Terms and Conditions</a>.
                                        This includes storing my email and encrypted password for authentication.
                                    </Text>
                                </Checkbox>
                            </Form.Item>

                            <Button type="primary" htmlType="submit" loading={loading} block size="large">
                                Create user account
                            </Button>
                        </div>
                    </Space>
                </Card>
            </Form>
        </div>
    );
};

export default RegisterForm;
